import * as React from 'react';

import {
  Button,
  DialogContent,
  DialogHeader,
  DialogOverlay,
} from '@rematter/ui-web';
import { Trash2 } from 'lucide-react';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';

interface VoidBookingDialogProps {
  bookingId: string;
  onVoid: (
    bookingId: string,
    forceUnlink?: boolean,
  ) => Promise<{
    blockingLoads?: Array<{ id: string; documentNumber: string }>;
    unlinkableLoads?: Array<{ id: string; documentNumber: string }>;
    requiresConfirmation?: boolean;
  }>;
  onCancel?: () => void;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
}

export const VoidBookingDialog: React.FC<VoidBookingDialogProps> = ({
  bookingId,
  onVoid,
  onCancel,
  message,
  confirmLabel,
  cancelLabel,
}) => {
  const t = useTranslations();
  const [blockingLoads, setBlockingLoads] = React.useState<
    Array<{ id: string; documentNumber: string }>
  >([]);
  const [unlinkableLoads, setUnlinkableLoads] = React.useState<
    Array<{ id: string; documentNumber: string }>
  >([]);
  const [showConfirmation, setShowConfirmation] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  // i18n fallbacks
  const i18nMessage = message ?? t.voidBookingDialog.message;
  const i18nConfirm = confirmLabel ?? t.voidBookingDialog.confirm;
  const i18nCancel = cancelLabel ?? t.voidBookingDialog.cancel;
  const i18nTitle = t.voidBookingDialog.title;

  const handleConfirm = async () => {
    try {
      setIsLoading(true);
      const result = await onVoid(bookingId, false);

      // Check if there are blocking loads
      if (result.blockingLoads && result.blockingLoads.length > 0) {
        setBlockingLoads(result.blockingLoads);
        return;
      }

      // Check if there are unlinkable loads requiring confirmation
      if (
        result.unlinkableLoads &&
        result.unlinkableLoads.length > 0 &&
        result.requiresConfirmation
      ) {
        setUnlinkableLoads(result.unlinkableLoads);
        setShowConfirmation(true);
        return;
      }
    } catch (error) {
      console.error('Error voiding booking:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleForceUnlink = async () => {
    try {
      setIsLoading(true);
      await onVoid(bookingId, true);
    } catch (error) {
      console.error('Error force unlinking booking:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Show blocking loads error
  if (blockingLoads.length > 0) {
    return (
      <DialogOverlay isOpen className="@VoidBookingDialog">
        <DialogContent>
          <DialogHeader>
            <div className="text-base font-semibold">Cannot Void Booking</div>
          </DialogHeader>
          <div className="flex flex-col gap-2 px-4">
            <div className="text-base text-neutral/50">
              This booking cannot be voided because the following loads have
              already been shipped, received, or reconciled:
            </div>
            <ul className="list-disc pl-5">
              {blockingLoads.map((load) => (
                <li key={load.id}>Load #{load.documentNumber}</li>
              ))}
            </ul>
            <div className="flex justify-end gap-2 py-4">
              <Button variant="solid" color="primary" onPress={onCancel}>
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </DialogOverlay>
    );
  }

  // Show unlinkable loads confirmation
  if (showConfirmation && unlinkableLoads.length > 0) {
    return (
      <DialogOverlay isOpen className="@VoidBookingDialog">
        <DialogContent>
          <DialogHeader>
            <div className="text-base font-semibold">
              Confirm Void with Load Unlinking
            </div>
          </DialogHeader>
          <div className="flex flex-col gap-2 px-4">
            <div className="text-base text-neutral/50">
              Voiding this booking will unlink the following pending/unassigned
              loads:
            </div>
            <ul className="list-disc pl-5">
              {unlinkableLoads.map((load) => (
                <li key={load.id}>Load #{load.documentNumber}</li>
              ))}
            </ul>
            <div className="mt-2 text-base text-neutral/50">
              Do you want to proceed with voiding and unlinking these loads?
            </div>
            <div className="flex justify-end gap-2 py-4">
              <Button variant="text" color="neutral" onPress={onCancel}>
                Cancel
              </Button>
              <Button
                variant="solid"
                color="failure"
                onPress={handleForceUnlink}
                icon={<Trash2 className="size-4" />}
                isDisabled={isLoading}
              >
                Void and Unlink
              </Button>
            </div>
          </div>
        </DialogContent>
      </DialogOverlay>
    );
  }

  // Initial confirmation dialog
  return (
    <DialogOverlay isOpen className="@VoidBookingDialog">
      <DialogContent>
        <DialogHeader>
          <div className="text-base font-semibold">{i18nTitle}</div>
        </DialogHeader>
        <div className="flex flex-col gap-2 px-4">
          <div className="text-base text-neutral/50">{i18nMessage}</div>
          <div className="flex justify-end gap-2 py-4">
            <Button variant="text" color="neutral" onPress={onCancel}>
              {i18nCancel}
            </Button>
            <Button
              variant="solid"
              color="failure"
              onPress={handleConfirm}
              icon={<Trash2 className="size-4" />}
              isDisabled={isLoading}
            >
              {i18nConfirm}
            </Button>
          </div>
        </div>
      </DialogContent>
    </DialogOverlay>
  );
};
